import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useCookieConsent } from "./CookieConsentContext";

const PRIMARY_BTN =
  "inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-full text-white dark:text-[#050505] bg-[#1E4FD8] dark:bg-[#4A90D9] transition-transform hover:scale-[1.02] active:scale-[0.98] active:brightness-90";
const SECONDARY_BTN =
  "inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-full text-[#0A0A0F] dark:text-white border border-[#0A0A0F]/15 dark:border-white/15 hover:border-[#1E4FD8] dark:hover:border-[#4A90D9] transition-colors";
const LINK_BTN = "text-sm font-medium text-[#0A0A0F]/70 dark:text-white/60 hover:text-[#0A0A0F] dark:hover:text-white underline transition-colors";

const PolicyLinks = () => (
  <>
    <Link to="/cookie-policy" className="underline hover:text-[#0A0A0F] dark:hover:text-white">Cookie Policy</Link>
    {" "}and{" "}
    <Link to="/privacy-policy" className="underline hover:text-[#0A0A0F] dark:hover:text-white">Privacy Policy</Link>.
  </>
);

export function CookieConsentBanner() {
  const { region, bannerOpen, acceptAll, rejectAll, openPreferences, acknowledge } = useCookieConsent();
  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(0);

  // The banner is `fixed`, so it takes no space in document flow — without this
  // spacer the page's scrollable height doesn't account for it, and the fixed
  // banner covers the last chunk of content the user can never scroll past.
  useEffect(() => {
    if (!bannerOpen || !bannerRef.current) return;
    const el = bannerRef.current;
    const observer = new ResizeObserver(([entry]) => setBannerHeight(entry.contentRect.height));
    observer.observe(el);
    return () => observer.disconnect();
  }, [bannerOpen]);

  if (!bannerOpen) return null;

  return (
    <>
      <div style={{ height: bannerHeight }} aria-hidden="true" />
      <div ref={bannerRef} className="fixed bottom-0 left-0 right-0 z-[60] bg-[#FAFAFA] dark:bg-[#0D0D10] border-t border-[#0A0A0F]/10 dark:border-white/10 shadow-[0_-8px_30px_rgba(0,0,0,0.08)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="max-w-2xl">
          <p className="text-sm font-medium text-[#0A0A0F] dark:text-white mb-1">Your privacy choices</p>
          <p className="text-sm text-[#5C5C66] dark:text-[#9A9A9A]">
            {region === "eea_uk_ch" && (
              <>
                We use cookies to run iViz and, with your consent, to measure usage and improve the
                Service. You can accept all, reject all, or choose by category. See our{" "}
                <PolicyLinks />
              </>
            )}
            {region === "us" && (
              <>
                We use cookies to run iViz and to measure usage. We do not sell or share your
                personal information, and you can turn off analytics at any time in Cookie settings.
                See our <PolicyLinks />
              </>
            )}
            {region === "row" && (
              <>
                We use cookies to run iViz and to measure usage. You can manage your choices at any
                time. See our <PolicyLinks />
              </>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 shrink-0">
          {region === "eea_uk_ch" && (
            <>
              <button onClick={rejectAll} className={SECONDARY_BTN}>Reject all</button>
              <button onClick={acceptAll} className={PRIMARY_BTN}>Accept all</button>
              <button onClick={openPreferences} className={LINK_BTN}>Manage preferences</button>
            </>
          )}
          {region === "us" && (
            <>
              <button onClick={openPreferences} className={SECONDARY_BTN}>Cookie settings</button>
              <button onClick={acknowledge} className={PRIMARY_BTN}>OK</button>
            </>
          )}
          {region === "row" && (
            <>
              <button onClick={openPreferences} className={SECONDARY_BTN}>Cookie settings</button>
              <button onClick={acknowledge} className={PRIMARY_BTN}>OK</button>
            </>
          )}
        </div>
      </div>
      </div>
    </>
  );
}
